import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, Label, FormGroup, CardTitle } from 'reactstrap';
import { Field, submit, getFormValues, change as changeFormValue } from 'redux-form';
import { Checkbox } from 'semantic-ui-react';
import { auto_status_bar_icons, normalize_status_bar_background, resolve_status_bar_contrast_background } from '../../../services/statusBarTheme';
import { Utils } from '../../../services';
import InlineImageField from '../../InlineImageField/InlineImageField';
import ColorPickerField from '../../ColorPickerField/ColorPickerField';
import SettingField from '../../SettingField/SettingField';
import UnitNumberField from '../../UnitNumberField/UnitNumberField';
import { changeSetting } from '../../../redux/modules/settings';
import { ExploritNavIcon } from '../exploritNavIcon';

import './ThemeSettings.css';
import '../exploritIcomoon.scss';
import '../../SettingsImagesField/SettingsImagesField.scss';

const CONTENT_COLOR_KEYS = ['background', 'primary', 'primary-inverse'];

/**
 * Normalize legacy single logo object to an array of { lang, rfc2397_data }.
 * Also coerces redux-form / merge array-likes (`{ 0: {...} }`) back to arrays —
 * otherwise upload updates vanish from the UI (normalize returned []).
 */
export function normalize_theme_logos(logo) {
    if (!logo)
        return [];
    if (Array.isArray(logo))
        return logo;
    if (typeof logo !== 'object')
        return [];

    const keys = Object.keys(logo);
    if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
        return keys
            .sort((a, b) => Number(a) - Number(b))
            .map((k) => logo[k])
            .filter((entry) => entry && typeof entry === 'object');
    }

    if (logo.rfc2397_data || logo.lang != null)
        return [{ lang: logo.lang ?? null, rfc2397_data: logo.rfc2397_data ?? null }];

    return [];
}

/** Default true: tint logo with primary. Legacy `keep_logo_colors` inverts. */
export function theme_applies_primary_to_logo(theme) {
    if (theme?.apply_primary_to_logo === false || theme?.apply_primary_to_logo === 'false')
        return false;
    if (theme?.keep_logo_colors === true || theme?.keep_logo_colors === 'true')
        return false;
    return true;
}

const ApplyPrimaryToLogoToggle = ({ input }) => {
    const checked = input.value !== false && input.value !== 'false';
    return (
        <div className="ThemeSettings-applyLogoSwitch">
            <Checkbox
                toggle
                checked={ checked }
                onChange={ (_e, { checked: next }) => input.onChange(!!next) }
            />
        </div>
    );
};

const ensure_theme = (setting, env) => {
    if (!setting.mobile)
        setting.mobile = {};
    if (!setting.mobile[env])
        setting.mobile[env] = {};
    if (!setting.mobile[env].theme)
        setting.mobile[env].theme = {};
    return setting.mobile[env].theme;
};

const STATUS_BAR_STYLES = ['auto', 'dark', 'light'];

const ThemeSection = ({ title, preview = null, children }) => (
    <div className="ThemeSettings-section">
        <div className="ThemeSettings-sectionHeader">
            <CardTitle tag="h6" className="ThemeSettings-subTitle mb-0">
                { title }
            </CardTitle>
            { preview }
        </div>
        <hr className="ThemeSettings-sectionRule" />
        <div className="ThemeSettings-sectionBody wrapper-1">
            { children }
        </div>
    </div>
);

const ThemeFormRow = ({ label, children, help }) => (
    <FormGroup row className="widget-setting ThemeSettings-formRow">
        <Label lg={ 4 } xl={ 3 }>
            { label }
        </Label>
        <Col xs={ 12 } lg={ 8 } xl={ 9 }>
            { children }
            { help ? (
                <p className="text-muted small mb-0 mt-1">{ help }</p>
            ) : null }
        </Col>
    </FormGroup>
);

const NAV_COLOR_KEYS = ['background', 'border', 'activeColor', 'inactiveColor'];

const ThemeSubSection = ({ title, help, children }) => (
    <>
        <CardTitle tag="h6" className="ThemeSettings-subSectionTitle">
            { title }
        </CardTitle>
        { help ? (
            <p className="text-muted small mb-2">{ help }</p>
        ) : null }
        { children }
    </>
);

/**
 * @param {'auto'|'dark'|'light'} style
 * @param {string} [contentBackground]
 * @param {string} [statusBarBackground]
 */
function status_bar_preview_colors(style, contentBackground, statusBarBackground) {
    const bar_bg = normalize_status_bar_background(statusBarBackground);
    const contrast_bg = resolve_status_bar_contrast_background(contentBackground, statusBarBackground);
    const iconsMode = style === 'auto' ? auto_status_bar_icons(contrast_bg) : style;
    const iconColor = iconsMode === 'dark' ? '#111111' : '#ffffff';

    if (!bar_bg) {
        return {
            showSwatch: style === 'auto',
            swatchColor: contentBackground || '#000000',
            stripBg: null,
            stripTransparent: true,
            iconColor,
        };
    }

    return {
        showSwatch: style === 'auto',
        swatchColor: bar_bg,
        stripBg: bar_bg,
        stripTransparent: false,
        iconColor,
    };
}

const StatusBarStylePreview = ({ style, contentBackground, statusBarBackground, backgroundImageUri }) => {
    const { showSwatch, swatchColor, stripBg, stripTransparent, iconColor } = status_bar_preview_colors(
        style,
        contentBackground,
        statusBarBackground
    );
    const show_transparent_image = stripTransparent && backgroundImageUri;
    const strip_class = [
        'ThemeSettings-statusBarPreview-icons',
        stripTransparent && !backgroundImageUri && 'ThemeSettings-statusBarPreview-icons--transparent',
        show_transparent_image && 'ThemeSettings-statusBarPreview-icons--bgImage',
    ].filter(Boolean).join(' ');

    return (
        <div className="ThemeSettings-statusBarPreview" aria-hidden="true">
            { showSwatch && (
                <>
                    <span
                        className="ThemeSettings-statusBarPreview-swatch"
                        style={{ backgroundColor: swatchColor }}
                    />
                    <span className="ThemeSettings-statusBarPreview-arrow">
                        <i className="fa fa-long-arrow-right" />
                    </span>
                </>
            ) }
            <span
                className={ strip_class }
                style={
                    show_transparent_image
                        ? {
                            backgroundImage: `url("${backgroundImageUri}")`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'top center',
                            color: iconColor,
                        }
                        : {
                            backgroundColor: stripBg || undefined,
                            color: iconColor,
                        }
                }
            >
                <i className="fa fa-signal" />
                <i className="fa fa-wifi" />
                <i className="fa fa-battery-full" />
                <span className="ThemeSettings-statusBarPreview-time">9:41</span>
            </span>
        </div>
    );
};

const NavBarPreview = ({ tabs, colors, lang, logoUri = '' }) => {
    const list = Array.isArray(tabs) ? tabs.filter(Boolean) : [];
    const raw_bg = String(colors?.background ?? '').trim();
    const bg = !raw_bg || raw_bg.toLowerCase() === 'transparent' ? 'transparent' : raw_bg;
    const border = colors?.border || '';
    const active = colors?.activeColor || '#ffffff';
    const inactive = colors?.inactiveColor || '#888888';
    const border_style = border
        ? { borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: border }
        : { borderTopWidth: 0, borderTopStyle: 'none' };

    if (!list.length) {
        return (
            <div
                className="ThemeSettings-navPreview ThemeSettings-navPreview--empty"
                style={{ backgroundColor: bg, ...border_style }}
                aria-hidden="true"
            />
        );
    }

    return (
        <div
            className="ThemeSettings-navPreview"
            style={{ backgroundColor: bg, ...border_style }}
            aria-hidden="true"
        >
            { list.map((tab, i) => {
                const color = i === 0 ? active : inactive;
                const label = Utils.localized_or_fallback(tab.name, lang) || '';
                return (
                    <div key={ i } className="ThemeSettings-navPreview-tab" style={{ color }}>
                        <ExploritNavIcon icon={ tab.icon } logoUri={ logoUri } />
                        { label ? (
                            <span className="ThemeSettings-navPreview-label">{ label }</span>
                        ) : null }
                    </div>
                );
            }) }
        </div>
    );
};

/**
 * Compact phone frame for nb_per_row + format ratio.
 */
const NbPerRowPreview = ({ nbPerRow, imageFormat }) => {
    const n = Number.isFinite(nbPerRow) && nbPerRow > 0 ? nbPerRow : 3;
    const ratio = Number.isFinite(imageFormat) && imageFormat > 0 ? imageFormat : (9 / 16);
    const visible = Math.min(Math.ceil(n), 10);
    const rowWidthPct = (visible / n) * 100;

    return (
        <div className="ThemeSettings-nbPerRowPhone" aria-hidden="true">
            <div className="ThemeSettings-nbPerRowPhone-bezel">
                <div className="ThemeSettings-nbPerRowPhone-screen">
                    <div
                        className="ThemeSettings-nbPerRowPhone-row"
                        style={{ width: `${rowWidthPct}%` }}
                    >
                        { Array.from({ length: visible }, (_, i) => (
                            <span
                                key={ i }
                                className="ThemeSettings-nbPerRowPhone-thumb"
                                style={{ aspectRatio: String(ratio) }}
                            />
                        )) }
                    </div>
                </div>
            </div>
        </div>
    );
};

const ThemeSettings = (props) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { env } = props;

    const formValues = useSelector(state => getFormValues('settingForm')(state));
    const theme = formValues?.mobile?.[env]?.theme ?? props.setting?.mobile?.[env]?.theme;
    const images = formValues?.mobile?.[env]?.images ?? props.setting?.mobile?.[env]?.images;
    const navTabs = formValues?.mobile?.[env]?.navigation?.tabs
        ?? props.setting?.mobile?.[env]?.navigation?.tabs;
    const rawLogo = theme?.logo;
    const logos = normalize_theme_logos(rawLogo);
    const storeIcon = formValues?.mobile?.[env]?.storeIcon
        ?? props.setting?.mobile?.[env]?.storeIcon;

    const statusBarStyle = theme?.statusBarStyle || 'auto';
    const statusBarBackgroundColor = theme?.statusBarBackgroundColor;
    const backgroundColor = theme?.colors?.background;
    const backgroundImageUri = theme?.backgroundImage?.rfc2397_data
        ?? props.setting?.mobile?.[env]?.theme?.backgroundImage?.rfc2397_data;
    const primaryColor = theme?.colors?.primary || '#ffffff';
    const primaryInverse = theme?.colors?.['primary-inverse'] || '#000000';
    const borderRadius = Number(theme?.style?.borderRadius) || 0;
    const imageFormat = Number(images?.format) || (9 / 16);
    const nbPerRow = Number(images?.nb_per_row) || 3;
    const formatSampleHeight = useMemo(() => {
        const ratio = Number.isFinite(imageFormat) && imageFormat > 0 ? imageFormat : (9 / 16);
        const height_em = 2.5 / ratio;
        return `${Math.min(4.5, Math.max(1.2, height_em))}rem`;
    }, [imageFormat]);

    useEffect(() => {
        if (!props.setting)
            return;

        const normalized = normalize_theme_logos(rawLogo);
        const needs_legacy_migrate = !Array.isArray(rawLogo) && Boolean(rawLogo?.rfc2397_data);
        const needs_array_coerce = rawLogo
            && !Array.isArray(rawLogo)
            && normalized.length > 0
            && Object.keys(rawLogo).every((k) => /^\d+$/.test(k));

        if (!needs_legacy_migrate && !needs_array_coerce)
            return;

        const newSetting = JSON.parse(JSON.stringify(props.setting));
        ensure_theme(newSetting, env).logo = normalized;
        dispatch(changeFormValue('settingForm', `mobile.${env}.theme.logo`, normalized));
        dispatch(changeSetting(newSetting));
    }, [env, rawLogo, props.setting, dispatch]);

    if (!props.setting)
        return null;

    const submitForm = () => {
        // Coerce array-like logo objects before save so Engine stores a real array.
        const normalized = normalize_theme_logos(
            formValues?.mobile?.[env]?.theme?.logo ?? rawLogo
        );
        dispatch(changeFormValue('settingForm', `mobile.${env}.theme.logo`, normalized));
        dispatch(submit('settingForm'));
    };

    const mutate_logos = (updater) => {
        const newSetting = JSON.parse(JSON.stringify(props.setting));
        const themeNode = ensure_theme(newSetting, env);
        const next = normalize_theme_logos(themeNode.logo);
        themeNode.logo = updater(next);
        // Keep redux-form logo as a real array (Field paths logo[i].…).
        dispatch(changeFormValue('settingForm', `mobile.${env}.theme.logo`, themeNode.logo));
        dispatch(changeSetting(newSetting));
    };

    const onAddLogo = () => {
        mutate_logos((list) => [...list, { lang: null, rfc2397_data: null }]);
    };

    const onRemoveLogo = (index) => {
        mutate_logos((list) => {
            const next = [...list];
            next.splice(index, 1);
            return next;
        });
    };

    const onDuplicateLogo = (index) => {
        mutate_logos((list) => {
            const source = list[index];
            if (!source)
                return list;
            return [...list, { ...source }];
        });
    };

    return (
        <div className="ThemeSettings">
            <ThemeSection title={ t('settings.mobile.images.title') }>
                <ThemeSubSection title={ t('settings.mobile.theme.logo') } help={ t('settings.mobile.theme.logo_help') }>
                    <Row>
                        { logos.map((image, i) => (
                            <Col key={ i } sm={ 12 } md={ 6 } lg={ 4 } xl={ 3 }>
                                <InlineImageField
                                    name={ `mobile.${env}.theme.logo[${i}]` }
                                    image={ image }
                                    hideType
                                    preset="mobileThemeLogo"
                                    onDropImage={ submitForm }
                                    onDuplicate={ () => onDuplicateLogo(i) }
                                    onRemove={ () => onRemoveLogo(i) }
                                />
                            </Col>
                        )) }
                        <Col key="add-logo" sm={ 12 } md={ 6 } lg={ 4 } xl={ 3 }>
                            <div className="add-button-wrapper" onClick={ onAddLogo }>
                                <div className="add-button">
                                    <i className="fa fa-plus"></i>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </ThemeSubSection>
                <Row className="mt-2">
                    <Col>
                        <ThemeSubSection
                            title={ t('settings.mobile.theme.splash') }
                            help={ t('settings.mobile.theme.splash_help') }
                        >
                            <InlineImageField
                                name={ `mobile.${env}.theme.splash` }
                                image={ theme?.splash }
                                hideOptions
                                preset="mobileTheme"
                            />
                        </ThemeSubSection>
                    </Col>
                    <Col>
                        <ThemeSubSection
                            title={ t('settings.mobile.theme.background_image') }
                            help={ t('settings.mobile.theme.background_image_help') }
                        >
                            <InlineImageField
                                name={ `mobile.${env}.theme.backgroundImage` }
                                image={ theme?.backgroundImage }
                                hideOptions
                                preset="mobileTheme"
                            />
                        </ThemeSubSection>
                    </Col>
                    <Col>
                        <ThemeSubSection
                            title={ t('settings.mobile.theme.store_icon') }
                            help={ t('settings.mobile.theme.store_icon_help') }
                        >
                            <InlineImageField
                                name={ `mobile.${env}.storeIcon` }
                                image={ storeIcon }
                                hideOptions
                                preset="storeIcon"
                            />
                        </ThemeSubSection>
                    </Col>
                </Row>
            </ThemeSection>

            <div className="SettingField">
                <ThemeSection
                    title={ t('settings.mobile.theme.status_bar.title') }
                    preview={
                        <StatusBarStylePreview
                            style={ statusBarStyle }
                            contentBackground={ backgroundColor }
                            statusBarBackground={ statusBarBackgroundColor }
                            backgroundImageUri={ backgroundImageUri }
                        />
                    }
                >
                    <ThemeFormRow
                        label={ t('settings.mobile.theme.status_bar_style') }
                    >
                        <Field
                            component="select"
                            className="form-control ThemeSettings-statusBarStyle"
                            name={ `mobile.${env}.theme.statusBarStyle` }
                            format={ (v) => v || 'auto' }
                            normalize={ (v) => v || 'auto' }
                        >
                            { STATUS_BAR_STYLES.map((value) => (
                                <option key={ value } value={ value }>
                                    { t(`settings.mobile.theme.status_bar_style_${value}`) }
                                </option>
                            )) }
                        </Field>
                    </ThemeFormRow>
                    <ThemeFormRow
                        label={ t('settings.mobile.theme.statusBarBackgroundColor') }
                        help={ t('settings.mobile.theme.status_bar_background_help') }
                    >
                        <ColorPickerField
                            name={ `mobile.${env}.theme.statusBarBackgroundColor` }
                            optional
                            clearable
                        />
                    </ThemeFormRow>
                </ThemeSection>

                <ThemeSection title={ t('settings.mobile.theme.colors.content') }>
                    { CONTENT_COLOR_KEYS.map((key) => (
                        key === 'primary' ? (
                            <ThemeFormRow
                                key={ `${env}-${key}` }
                                label={ t('settings.mobile.theme.colors.primary') }
                            >
                                <div className="ThemeSettings-controlRow ThemeSettings-primaryRow">
                                    <ColorPickerField name={ `mobile.${env}.theme.colors.primary` } />
                                    <div className="ThemeSettings-applyLogo">
                                        <Field
                                            name={ `mobile.${env}.theme.apply_primary_to_logo` }
                                            component={ ApplyPrimaryToLogoToggle }
                                        />
                                        <span className="ThemeSettings-applyLogoLabel">
                                            { t('settings.mobile.theme.apply_primary_to_logo') }
                                        </span>
                                    </div>
                                </div>
                            </ThemeFormRow>
                        ) : (
                            <SettingField
                                { ...props }
                                key={ `${env}-${key}` }
                                showTitle
                                name={ `mobile.${env}.theme.colors.${key}` }
                            />
                        )
                    )) }
                    <ThemeFormRow
                        label={ t('settings.mobile.theme.style.border_radius') }
                        help={ t('settings.mobile.theme.style.border_radius_help') }
                    >
                        <div className="ThemeSettings-controlRow">
                            <Field
                                component={ UnitNumberField }
                                name={ `mobile.${env}.theme.style.borderRadius` }
                                min={ 0 }
                                max={ 45 }
                                unit="px"
                            />
                            <button
                                type="button"
                                className="ThemeSettings-buttonSample"
                                tabIndex={ -1 }
                                style={{
                                    backgroundColor: primaryColor,
                                    color: primaryInverse,
                                    borderRadius: `${borderRadius}px`,
                                }}
                            >
                                { t('settings.mobile.theme.style.border_radius_sample') }
                            </button>
                        </div>
                    </ThemeFormRow>
                    <ThemeFormRow
                        label={ t('settings.mobile.images.format') }
                        help={ t('settings.mobile.images.format_help') }
                    >
                        <div className="ThemeSettings-controlRow">
                            <Field
                                component="input"
                                className="form-control ThemeSettings-compactInput"
                                type="number"
                                step="any"
                                min={ 0.1 }
                                max={ 4 }
                                name={ `mobile.${env}.images.format` }
                            />
                            <span
                                className="ThemeSettings-formatSample"
                                style={{ height: formatSampleHeight }}
                                aria-hidden="true"
                            />
                        </div>
                    </ThemeFormRow>
                    <ThemeFormRow
                        label={ t('settings.mobile.images.nb_per_row') }
                        help={ t('settings.mobile.images.nb_per_row_help') }
                    >
                        <div className="ThemeSettings-controlRow">
                            <Field
                                component="input"
                                className="form-control ThemeSettings-compactInput"
                                type="number"
                                step="any"
                                min={ 0.5 }
                                max={ 10 }
                                name={ `mobile.${env}.images.nb_per_row` }
                            />
                            <NbPerRowPreview nbPerRow={ nbPerRow } imageFormat={ imageFormat } />
                        </div>
                    </ThemeFormRow>
                </ThemeSection>

                <ThemeSection
                    title={ t('settings.mobile.theme.colors.nav.title') }
                    preview={
                        <NavBarPreview
                            tabs={ navTabs }
                            colors={ theme?.colors?.nav }
                            lang={ i18n.language }
                            logoUri={ logos.find((logo) => logo?.rfc2397_data)?.rfc2397_data || '' }
                        />
                    }
                >
                    { NAV_COLOR_KEYS.map((key) => (
                        <SettingField
                            { ...props }
                            key={ `${env}-${key}` }
                            showTitle
                            name={ `mobile.${env}.theme.colors.nav.${key}` }
                        />
                    )) }
                </ThemeSection>
            </div>
        </div>
    );
}

export default ThemeSettings;
